// Pure aggregations for OperationalDashboardView. Input is the response of
// GET /ops-dashboard: rows (Day × Machine × Customer × Currency, net of VAT),
// customers, machines (current snapshot) and incidences.

export const UNKNOWN = 'Sin dato'
export const NO_CUSTOMER = 'Sin cliente'

const BUSINESS_MODEL_LABELS = {
  'REVENUE SHARE': 'Revenue share',
  'SPACE RENTAL': 'Space rental',
  'RENTAL': 'Alquiler',
  'SALE': 'Venta',
}

export function businessModelLabel(bm) {
  return BUSINESS_MODEL_LABELS[String(bm || '').trim().toUpperCase()] || bm || UNKNOWN
}

export const PRODUCT_LABELS = {
  VWPACK: 'Pack',
  VWLAY: 'Lay',
  VWGO: 'Go',
  LOCKERBASIC: 'Taquilla',
  LOCKERBASICXL: 'Taquilla XL',
  LOCKERCHARGER: 'Taquilla cargador',
  LOCKERCHARGERXL: 'Taquilla cargador XL',
  TUMBONAS: 'Tumbonas',
  FLOTADORES: 'Flotadores',
  FLOTADORESHALFDAY: 'Flotadores ½ día',
  FASTPASS: 'Fastpass',
}

export const PAYMENT_LABELS = {
  CASH: 'Efectivo',
  TPV: 'Tarjeta',
  RFID: 'RFID',
  QR: 'QR',
  TOKEN: 'Manual / token',
  NULL: 'Sin método',
}

export const ONLINE_WINDOW_MS = 5 * 60 * 1000
export const CASH_FULL_THRESHOLD = 1500

export function buildContext(data) {
  const customersById = new Map()
  const machinesById = new Map()
  ;((data && data.customers) || []).forEach(c => customersById.set(c._id, c))
  ;((data && data.machines) || []).forEach(m => machinesById.set(m._id, m))
  return { customersById, machinesById }
}

export function customerOf(ctx, id) {
  return (id && ctx.customersById.get(id)) || null
}

export function customerName(ctx, id) {
  const c = customerOf(ctx, id)
  return c ? (c.Fullname || UNKNOWN) : NO_CUSTOMER
}

export function machineSerial(ctx, id) {
  const m = id && ctx.machinesById.get(id)
  return (m && m.Serial) || '—'
}

function attr(ctx, customerId, key) {
  const c = customerOf(ctx, customerId)
  return (c && c[key]) || UNKNOWN
}

export function isSaleModel(bm) {
  return String(bm || '').trim().toUpperCase() === 'SALE'
}

// filters: { customerId, text, country, category, businessModel, currency }.
// All filters combine (AND): a picked customer still honours country/model.
export function matchesCustomer(ctx, customerId, machineId, filters) {
  if (filters.customerId && customerId !== filters.customerId) return false
  const q = filters.customerId ? '' : (filters.text || '').trim().toLowerCase()
  if (q) {
    const name = customerName(ctx, customerId).toLowerCase()
    const serial = machineSerial(ctx, machineId).toLowerCase()
    if (!name.includes(q) && !serial.includes(q)) return false
  }
  if (filters.country && attr(ctx, customerId, 'Country') !== filters.country) return false
  if (filters.category && attr(ctx, customerId, 'Category') !== filters.category) return false
  if (filters.businessModel && attr(ctx, customerId, 'BusinessModel') !== filters.businessModel) return false
  return true
}

export function filterRows(rows, ctx, filters) {
  return (rows || []).filter(r =>
    (!filters.currency || r.Currency === filters.currency) &&
    matchesCustomer(ctx, r.CustomerId, r.MachineId, filters)
  )
}

// Installed = machine assigned to a customer right now (snapshot, not history).
export function filterInstalledMachines(machines, ctx, filters) {
  return (machines || []).filter(m =>
    m.CustomerId &&
    (!filters.currency || !m.Currency || m.Currency === filters.currency) &&
    matchesCustomer(ctx, m.CustomerId, m._id, filters)
  )
}

export function filterIncidences(incidences, ctx, filters) {
  return (incidences || []).filter(i => matchesCustomer(ctx, i.CustomerId, i.MachineId, filters))
}

// Rows of a SALE customer bring no Vacway margin: excluded from commission
// and from the take-rate base (same rule as the pro dashboard).
export function aggregate(rows, ctx) {
  let gmv = 0, gross = 0, transactions = 0, manual = 0, refunds = 0, refundsNet = 0
  let commission = 0, takeBase = 0
  const machines = new Set()
  const machineDays = new Set()
  const units = {}
  rows.forEach(r => {
    gmv += r.Net
    gross += r.Gross
    transactions += r.Tickets
    manual += r.Manual
    refunds += r.Refunds
    refundsNet += r.RefundsNet
    if (!isSaleModel(attr(ctx, r.CustomerId, 'BusinessModel'))) {
      commission += r.VacwayNet
      takeBase += r.Net
    }
    if (r.Tickets > 0 && r.MachineId) {
      machines.add(r.MachineId)
      machineDays.add(`${r.Day}|${r.MachineId}`)
    }
    Object.entries(r.Units || {}).forEach(([k, v]) => { units[k] = (units[k] || 0) + v })
  })
  return {
    gmv, gross, transactions, manual, refunds, refundsNet, commission, units,
    ticketAvg: transactions > 0 ? gmv / transactions : null,
    takeRate: takeBase > 0 ? commission / takeBase : null,
    activeMachines: machines.size,
    activeMachineIds: machines,
    machineDays: machineDays.size,
    gmvPerActiveMachine: machines.size > 0 ? gmv / machines.size : null,
    gmvPerMachineDay: machineDays.size > 0 ? gmv / machineDays.size : null,
    commissionPerActiveMachine: machines.size > 0 ? commission / machines.size : null,
  }
}

function dayStart(iso) { return new Date(`${iso}T00:00:00.000Z`).getTime() }
function dayEndExclusive(iso) { return dayStart(iso) + 86400000 }

function inRange(date, startIso, endIso) {
  if (!date) return false
  const t = new Date(date).getTime()
  return t >= dayStart(startIso) && t < dayEndExclusive(endIso)
}

// opened/resolved in the period, MTTR (h) of those resolved in the period,
// openNow = still not RESOLVED (snapshot).
export function incidenceStats(incidences, startIso, endIso) {
  let opened = 0, resolved = 0, openNow = 0, repairMs = 0, repairN = 0
  incidences.forEach(i => {
    if (inRange(i.OpenAt, startIso, endIso)) opened += 1
    if (i.Status === 'RESOLVED' && inRange(i.ResolvedAt, startIso, endIso)) {
      resolved += 1
      if (i.OpenAt) {
        const ms = new Date(i.ResolvedAt) - new Date(i.OpenAt)
        if (ms >= 0) { repairMs += ms; repairN += 1 }
      }
    }
    if (i.Status !== 'RESOLVED') openNow += 1
  })
  return { opened, resolved, openNow, mttrHours: repairN > 0 ? repairMs / repairN / 3600000 : null }
}

export function incidencesOpenedBy(incidences, startIso, endIso, keyFn) {
  const out = {}
  incidences.forEach(i => {
    if (!inRange(i.OpenAt, startIso, endIso)) return
    const k = keyFn(i)
    out[k] = (out[k] || 0) + 1
  })
  return out
}

// Same mapping as utils/machines.getMachineStatus, without mutating the machine.
export function machineStatus(m, now = Date.now()) {
  if (!m.Last_signal) return 'UNKNOWN'
  if (now - new Date(m.Last_signal).getTime() >= ONLINE_WINDOW_MS) return 'OFFLINE'
  switch (m.Status) {
    case 'OPERATIVE': return 'ONLINE'
    case 'OPERATIVE_SLEEP': return 'SLEEP ONLINE'
    case 'SLEEP': return 'SLEEP'
    case 'NIGHT_SCAN': return 'NIGHTSCAN'
    case 'MAINTENANCE': return 'MANTENIMIENTO'
    case 'MAINTENANCE_SLEEP': return 'SLEEP MAINTENANCE'
    case 'LOCKERS_FULL': return 'LOCKERS LLENOS'
    case 'TICKET_EMPTY': return 'SIN TICKET'
    default: return 'UNKNOWN'
  }
}

// Reachable and able to sell (sleep counts as healthy: it is scheduled).
export function isMachineUp(status) {
  return ['ONLINE', 'SLEEP ONLINE', 'SLEEP', 'NIGHTSCAN'].includes(status)
}

export function groupByCustomer(rows, ctx) {
  const out = new Map()
  rows.forEach(r => {
    const key = r.CustomerId || 'none'
    let g = out.get(key)
    if (!g) {
      g = { customerId: r.CustomerId, name: customerName(ctx, r.CustomerId), rows: [] }
      out.set(key, g)
    }
    g.rows.push(r)
  })
  return out
}

// Share of installed machines (or of GMV) per customer attribute.
export function installedBy(machines, ctx, key) {
  const out = {}
  machines.forEach(m => {
    const k = attr(ctx, m.CustomerId, key)
    out[k] = (out[k] || 0) + 1
  })
  return toSlices(out)
}

// Machines with sales in the period (same set as aggregate().activeMachines),
// grouped by the customer they sold for — a machine may have no customer today.
export function activeMachinesBy(rows, ctx, key) {
  const customerByMachine = new Map()
  rows
    .filter(r => r.Tickets > 0 && r.MachineId)
    .sort((a, b) => a.Day.localeCompare(b.Day))
    .forEach(r => customerByMachine.set(r.MachineId, r.CustomerId)) // latest day wins
  const out = {}
  customerByMachine.forEach(customerId => {
    const k = attr(ctx, customerId, key)
    out[k] = (out[k] || 0) + 1
  })
  return toSlices(out)
}

export function gmvBy(rows, ctx, key) {
  const out = {}
  rows.forEach(r => {
    const k = attr(ctx, r.CustomerId, key)
    out[k] = (out[k] || 0) + r.Net
  })
  return toSlices(out)
}

export function sumMap(rows, field, labels) {
  const out = {}
  rows.forEach(r => {
    Object.entries(r[field] || {}).forEach(([k, v]) => {
      const name = (labels && labels[k]) || k
      out[name] = (out[name] || 0) + v
    })
  })
  return toSlices(out)
}

export function toSlices(obj, top = 6) {
  const all = Object.entries(obj)
    .map(([name, value]) => ({ name, value }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value)
  if (all.length <= top + 1) return all
  const rest = all.slice(top).reduce((s, d) => s + d.value, 0)
  return [...all.slice(0, top), { name: 'Otros', value: rest }]
}

export function daysBetween(startIso, endIso) {
  const out = []
  if (!startIso || !endIso) return out
  for (let t = dayStart(startIso); t < dayEndExclusive(endIso); t += 86400000) {
    out.push(new Date(t).toISOString().slice(0, 10))
  }
  return out
}

// Cumulative GMV and Vacway commission per day; the comparison series is
// aligned by day index (day 1 vs day 1) so ranges of different years overlay.
export function cumulativeTrend(rows, cmpRows, ctx, range, cmpRange) {
  const perDay = (rs) => {
    const m = {}
    rs.forEach(r => {
      const d = m[r.Day] || (m[r.Day] = { gmv: 0, commission: 0 })
      d.gmv += r.Net
      if (!isSaleModel(attr(ctx, r.CustomerId, 'BusinessModel'))) d.commission += r.VacwayNet
    })
    return m
  }
  const cur = perDay(rows)
  const cmp = perDay(cmpRows || [])
  const days = daysBetween(range.start, range.end)
  const cmpDays = cmpRange ? daysBetween(cmpRange.start, cmpRange.end) : []
  const n = Math.max(days.length, cmpDays.length)
  let g = 0, c = 0, lg = 0, lc = 0
  const out = []
  for (let i = 0; i < n; i++) {
    const p = { idx: i + 1, day: days[i] || null, cmpDay: cmpDays[i] || null }
    if (days[i]) {
      g += (cur[days[i]] || {}).gmv || 0
      c += (cur[days[i]] || {}).commission || 0
      p.gmv = g
      p.commission = c
    }
    if (cmpDays[i]) {
      lg += (cmp[cmpDays[i]] || {}).gmv || 0
      lc += (cmp[cmpDays[i]] || {}).commission || 0
      p.cmpGmv = lg
      p.cmpCommission = lc
    }
    out.push(p)
  }
  return out
}

export function isoDay(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

export function shiftYear(iso, delta) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  const target = new Date(y + delta, m - 1, 1)
  const lastDay = new Date(y + delta, m, 0).getDate()
  target.setDate(Math.min(d, lastDay)) // 29 Feb → 28 Feb
  return isoDay(target)
}

// Normalizes the advanced-filter inputs into a [start, end] day range.
// periodType: 'range' | 'day' | 'month' (value YYYY-MM) | 'year' (value YYYY)
export function normalizePeriod(periodType, a, b) {
  if (periodType === 'day') return a ? { start: a, end: a } : null
  if (periodType === 'month') {
    if (!/^\d{4}-\d{2}$/.test(a || '')) return null
    const [y, m] = a.split('-').map(Number)
    return { start: isoDay(new Date(y, m - 1, 1)), end: isoDay(new Date(y, m, 0)) }
  }
  if (periodType === 'year') {
    const y = Number(a)
    if (!y) return null
    return { start: `${y}-01-01`, end: `${y}-12-31` }
  }
  if (!a || !b || b < a) return null
  return { start: a, end: b }
}

// Never look past today: a current month/year would otherwise be compared,
// half empty, against a complete period. Null when it starts in the future.
export function capToToday(period, todayIso = isoDay(new Date())) {
  if (!period) return null
  if (period.start > todayIso) return null
  return { start: period.start, end: period.end > todayIso ? todayIso : period.end }
}

export function pctChange(cur, prev) {
  if (prev == null || prev === 0 || cur == null) return null
  return (cur - prev) / Math.abs(prev)
}
